// src/components/admin/AdminPendingCondosPage.tsx
// Admin page for reviewing user-submitted condos pending approval.

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import BackgroundShapes from '../common/BackgroundShapes';
import { condoService } from '../../services/condoService';
import { Condo } from '../../types/condo';

function getSubmitterName(condo: Condo): string {
  if (!condo.submittedBy) return 'Unknown';
  if (typeof condo.submittedBy === 'string') return condo.submittedBy;
  const s = condo.submittedBy;
  const firstName = s.personalInfo?.firstName ?? '';
  const lastName = s.personalInfo?.lastName ?? '';
  const username = s.account?.username ?? '';
  const fullName = [firstName, lastName].filter(Boolean).join(' ');
  return fullName || username || 'Unknown';
}

const AdminPendingCondosPage: React.FC = () => {
  const navigate = useNavigate();
  const [condos, setCondos] = useState<Condo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Track inline decline reason per condo
  const [declineInputs, setDeclineInputs] = useState<Record<string, string>>({});
  const [showDeclineInput, setShowDeclineInput] = useState<Record<string, boolean>>({});
  const [processing, setProcessing] = useState<string | null>(null);

  const loadPending = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await condoService.getPendingCondos();
      if (result.success && result.data) {
        setCondos(result.data.condos);
      } else {
        setError('Failed to load pending condos.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load pending condos.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPending();
  }, [loadPending]);

  const handlePublish = async (condoId: string) => {
    setProcessing(condoId);
    try {
      const result = await condoService.reviewCondo(condoId, 'approve');
      if (result.success) {
        setCondos((prev) => prev.filter((c) => (c.id ?? c._id) !== condoId));
      } else {
        alert((result as any).message || 'Failed to approve condo.');
      }
    } catch (err: any) {
      alert(err.message || 'Failed to approve condo.');
    } finally {
      setProcessing(null);
    }
  };

  const handleDeclineSubmit = async (condoId: string) => {
    const reason = declineInputs[condoId]?.trim();
    if (!reason) {
      alert('Please enter a reason for declining.');
      return;
    }
    setProcessing(condoId);
    try {
      const result = await condoService.reviewCondo(condoId, 'decline', reason);
      if (result.success) {
        setCondos((prev) => prev.filter((c) => (c.id ?? c._id) !== condoId));
        setDeclineInputs((prev) => { const next = { ...prev }; delete next[condoId]; return next; });
        setShowDeclineInput((prev) => { const next = { ...prev }; delete next[condoId]; return next; });
      } else {
        alert((result as any).message || 'Failed to decline condo.');
      }
    } catch (err: any) {
      alert(err.message || 'Failed to decline condo.');
    } finally {
      setProcessing(null);
    }
  };

  return (
    <div className="min-h-screen bg-paper relative">
      <BackgroundShapes />

      <div className="container mx-auto px-4 py-8 relative z-10">
        {/* Header */}
        <div className="flex flex-wrap justify-between items-center gap-4 mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="bg-cream border-2 border-coffee text-coffee px-3 py-2 rounded-lg hover:bg-white transition-colors shadow-sm text-sm font-medium"
            >
              ← Dashboard
            </button>
            <div>
              <h1 className="text-3xl font-bold text-coffee mb-1">Review Submissions</h1>
              <p className="text-coffee opacity-80 text-sm">
                Approve or decline user-submitted condo listings.
              </p>
            </div>
          </div>
        </div>

        {loading && (
          <div className="text-center py-12 text-coffee">Loading pending condos…</div>
        )}

        {error && (
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-6 text-red-800">
            {error}
          </div>
        )}

        {!loading && !error && condos.length === 0 && (
          <div className="text-center py-12">
            <p className="text-coffee text-xl mb-2">No pending submissions</p>
            <p className="text-coffee opacity-80">All caught up!</p>
          </div>
        )}

        {!loading && !error && condos.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {condos.map((condo) => {
              const condoId = condo.id ?? condo._id ?? '';
              const isProcessing = processing === condoId;
              const showDecline = showDeclineInput[condoId] ?? false;

              return (
                <div
                  key={condoId}
                  className="bg-cream border-2 border-coffee rounded-xl p-5 shadow-sm"
                >
                  <div className="flex justify-between items-start mb-3">
                    <h2 className="text-lg font-bold text-coffee leading-tight flex-1 mr-2">
                      {condo.name}
                    </h2>
                    <span className="inline-block bg-yellow-100 border border-yellow-400 text-yellow-800 text-xs font-semibold px-2 py-0.5 rounded flex-shrink-0">
                      Pending
                    </span>
                  </div>

                  <p className="text-coffee opacity-70 text-sm mb-1">
                    {condo.location.borough}
                    {condo.location.neighborhood ? ` · ${condo.location.neighborhood}` : ''}
                  </p>

                  {condo.price != null && (
                    <p className="text-coffee text-sm mb-1">
                      ${condo.price.toLocaleString()} / mo
                    </p>
                  )}

                  {/* Submitter */}
                  <p className="text-coffee opacity-60 text-xs mb-3">
                    Submitted by: <span className="font-semibold">{getSubmitterName(condo)}</span>
                    {condo.createdAt ? ` · ${new Date(condo.createdAt).toLocaleDateString()}` : ''}
                  </p>

                  {/* Description */}
                  {condo.details?.description && (
                    <p className="text-coffee text-sm mb-3 bg-white border border-coffee rounded p-2">
                      {condo.details.description}
                    </p>
                  )}

                  {/* Action buttons */}
                  {!showDecline ? (
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => navigate(`/admin/condos/preview/${condoId}`)}
                        className="flex-1 bg-steel text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#6a94a6] transition-all border-2 border-coffee shadow-sm"
                      >
                        View Detail
                      </button>
                      <button
                        onClick={() => handlePublish(condoId)}
                        disabled={isProcessing}
                        className="flex-1 bg-terracotta text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#d89050] transition-all border-2 border-coffee shadow-sm disabled:opacity-50"
                      >
                        {isProcessing ? 'Publishing…' : 'Publish'}
                      </button>
                      <button
                        onClick={() => setShowDeclineInput((prev) => ({ ...prev, [condoId]: true }))}
                        disabled={isProcessing}
                        className="flex-1 bg-white border-2 border-coffee text-coffee px-4 py-2 rounded-lg hover:bg-cream transition-colors shadow-sm disabled:opacity-50"
                      >
                        Decline
                      </button>
                    </div>
                  ) : (
                    <div className="mt-3">
                      <textarea
                        value={declineInputs[condoId] ?? ''}
                        onChange={(e) =>
                          setDeclineInputs((prev) => ({ ...prev, [condoId]: e.target.value }))
                        }
                        placeholder="Reason for declining…"
                        rows={2}
                        className="w-full border-2 border-coffee rounded-lg px-3 py-2 bg-white text-coffee text-sm focus:outline-none focus:ring-2 focus:ring-terracotta resize-none mb-2"
                        disabled={isProcessing}
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleDeclineSubmit(condoId)}
                          disabled={isProcessing}
                          className="flex-1 bg-coffee text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#4a3b33] transition-all border-2 border-coffee shadow-sm disabled:opacity-50"
                        >
                          {isProcessing ? 'Declining…' : 'Confirm Decline'}
                        </button>
                        <button
                          onClick={() => setShowDeclineInput((prev) => ({ ...prev, [condoId]: false }))}
                          disabled={isProcessing}
                          className="flex-1 bg-white border-2 border-coffee text-coffee px-4 py-2 rounded-lg hover:bg-cream transition-colors shadow-sm disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPendingCondosPage;
